import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import axios from "axios";
import moment from "moment";
import { useDispatch } from "react-redux";
import { getPlayerBalance } from "../../store/slices/user";
import styles from "./RefundTab.module.css";

const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL || "").replace(
  /\/+$/,
  "",
);

export default function RefundTab() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState("info");
  const [refundHistory, setRefundHistory] = useState([]);

  const resolveUserId = useCallback(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) return parsed.id;
      }
    } catch (_) {}

    return localStorage.getItem("userId") || null;
  }, []);

  const uid = resolveUserId();

  const [refundData, setRefundData] = useState({
    availableAmount: 0,
    lastEligibleDate: null,
    eligibleForRefund: false,
    refundPercentage: 0,
    pendingCount: 0,
    paidFrequency: "-",
    cashBackDetail: "",
  });

  const formattedAmount = useMemo(
    () =>
      Number(refundData.availableAmount || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [refundData.availableAmount],
  );

  const loadRefundData = useCallback(async () => {
    if (!uid || !API_BASE_URL) {
      setRefundData({
        availableAmount: 0,
        lastEligibleDate: null,
        eligibleForRefund: false,
        refundPercentage: 0,
        pendingCount: 0,
        paidFrequency: "-",
        cashBackDetail: "",
      });
      setRefundHistory([]);
      return;
    }

    setIsFetching(true);
    try {
      const [settingRes, cashbackRes, historyRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/setting/cashback`),
        axios.get(`${API_BASE_URL}/agentbalance/cashbackclientcheck/${uid}`),
        axios.get(`${API_BASE_URL}/agentbalance/cashbacksummaryclient/${uid}`),
      ]);

      const cashbackSetting = Array.isArray(settingRes?.data?.data)
        ? settingRes.data.data[0] || {}
        : {};
      const cashbackRows = Array.isArray(cashbackRes?.data?.data)
        ? cashbackRes.data.data
        : [];
      const historyRows = Array.isArray(historyRes?.data?.data)
        ? historyRes.data.data
        : [];
      const availableAmount = cashbackRows.reduce(
        (total, row) => total + Number(row?.cashBackAmount || 0),
        0,
      );
      const latestRow = cashbackRows
        .slice()
        .sort(
          (a, b) =>
            new Date(b?.created_at || b?.cashBackDate || 0) -
            new Date(a?.created_at || a?.cashBackDate || 0),
        )[0];
      const latestHistoryRow = historyRows
        .slice()
        .sort(
          (a, b) =>
            new Date(b?.created_at || b?.cashBackDate || 0) -
            new Date(a?.created_at || a?.cashBackDate || 0),
        )[0];

      setRefundData({
        availableAmount,
        lastEligibleDate:
          latestRow?.cashBackDate ||
          latestRow?.created_at ||
          latestHistoryRow?.cashBackDate ||
          latestHistoryRow?.created_at ||
          null,
        eligibleForRefund: availableAmount > 0,
        refundPercentage: Number(
          cashbackSetting?.paidpercent || cashbackRows[0]?.cashBackPercent || 0,
        ),
        pendingCount: cashbackRows.length,
        paidFrequency: cashbackSetting?.paidfrequency || "-",
        cashBackDetail: cashbackSetting?.cashBackDetail || "",
      });
      setRefundHistory(
        historyRows
          .slice()
          .sort(
            (a, b) =>
              new Date(b?.created_at || b?.cashBackDate || 0) -
              new Date(a?.created_at || a?.cashBackDate || 0),
          ),
      );
      setFeedback("");
    } catch (error) {
      setRefundData({
        availableAmount: 0,
        lastEligibleDate: null,
        eligibleForRefund: false,
        refundPercentage: 0,
        pendingCount: 0,
        paidFrequency: "-",
        cashBackDetail: "",
      });
      setRefundHistory([]);
      setFeedback("ไม่สามารถโหลดข้อมูลคืนยอดเสียได้");
      setFeedbackType("error");
    } finally {
      setIsFetching(false);
    }
  }, [uid]);

  const statusLabel = useCallback((status) => {
    switch (String(status || "").toUpperCase()) {
      case "RECEIVED":
        return "รับแล้ว";
      case "UNRECEIVE":
        return "รอรับ";
      case "TIMEOUT":
        return "หมดอายุ";
      default:
        return status || "-";
    }
  }, []);

  useEffect(() => {
    loadRefundData();
  }, [loadRefundData]);

  const handleClaimRefund = useCallback(async () => {
    if (!refundData.eligibleForRefund || isLoading || !uid) return;

    setIsLoading(true);
    setFeedback("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/agentbalance/paidcashback/${uid}`,
      );
      if (res?.data?.success === true) {
        setFeedback("รับเงินคืนเรียบร้อยแล้ว");
        setFeedbackType("success");
        try {
          dispatch(getPlayerBalance({ id: uid }));
        } catch (_) {}
        await loadRefundData();
      } else {
        setFeedback(res?.data?.message || "ยังไม่สามารถรับเงินคืนได้");
        setFeedbackType("error");
      }
    } catch (error) {
      setFeedback("ไม่สามารถรับเงินคืนได้ กรุณาลองใหม่อีกครั้ง");
      setFeedbackType("error");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, isLoading, loadRefundData, refundData.eligibleForRefund, uid]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2Z"
              fill="currentColor"
            />
            <path
              d="M8 12L11 15L16 10"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h4 className={styles.title}>💰 คืนยอดเสีย</h4>
        <p className={styles.subtitle}>รับเงินคืนจากยอดเสียของท่าน</p>
      </div>

      <div className={styles.mainCard}>
        <div className={styles.promoHeader}>
          <div className={styles.promoBadge}>
            <Icon icon="solar:gift-bold" width={18} height={18} />
            <span>โปรโมชั่นพิเศษ</span>
          </div>
          <div className={styles.promoTitle}>
            <h3>คืนยอดเสีย {refundData.refundPercentage}%</h3>
          </div>
        </div>

        <div className={styles.detailsGrid}>
          <div className={styles.detailCard}>
            <div className={styles.detailIcon}>
              <Icon icon="solar:calendar-bold-duotone" width={32} height={32} />
            </div>
            <div className={styles.detailContent}>
              <div className={styles.detailLabel}>รอบการคืน</div>
              <div className={styles.detailValue}>
                {refundData.paidFrequency}
              </div>
            </div>
          </div>

          <div className={styles.detailCard}>
            <div className={styles.detailIcon}>
              <Icon icon="solar:ticket-bold-duotone" width={32} height={32} />
            </div>
            <div className={styles.detailContent}>
              <div className={styles.detailLabel}>จำนวนรายการรอรับ</div>
              <div className={styles.detailValue}>
                {refundData.pendingCount.toLocaleString("th-TH")} รายการ
              </div>
            </div>
          </div>
        </div>

        <div className={styles.claimAmountSection}>
          <div className={styles.claimAmountLabel}>จำนวนเงินที่รับได้</div>
          <div className={styles.claimAmountValue}>
            <span className={styles.claimCurrency}>฿</span>
            <span className={styles.claimAmount}>{formattedAmount}</span>
          </div>
        </div>

        {refundData.cashBackDetail ? (
          <div className={styles.feedbackNote}>{refundData.cashBackDetail}</div>
        ) : null}

        {feedback ? (
          <div
            className={`${styles.feedbackMessage} ${
              feedbackType === "success"
                ? styles.feedbackSuccess
                : styles.feedbackError
            }`}
          >
            {feedback}
          </div>
        ) : null}

        {refundData.eligibleForRefund ? (
          <button
            className={`${styles.claimButton} ${
              isLoading ? styles.loading : ""
            }`}
            onClick={handleClaimRefund}
            disabled={isLoading || isFetching}
          >
            <span className={styles.buttonContent}>
              {isLoading ? (
                <>
                  <div className={styles.spinner}></div>
                  กำลังดำเนินการ...
                </>
              ) : (
                <>
                  <svg
                    className={styles.buttonIcon}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 10L12 15L17 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  รับเงินคืนทันที
                </>
              )}
            </span>
          </button>
        ) : (
          <div className={styles.notEligible}>
            <div className={styles.notEligibleIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={styles.notEligibleText}>
              <h4>
                {isFetching
                  ? "กำลังโหลดข้อมูลคืนยอดเสีย"
                  : "ยังไม่สามารถรับเงินคืนได้"}
              </h4>
              <p>
                {isFetching
                  ? "ระบบกำลังตรวจสอบรายการคืนยอดเสียของท่าน"
                  : "ขออภัยท่านไม่มียอดเสียที่สามารถรับเงินคืนได้ในขณะนี้"}
              </p>
            </div>
          </div>
        )}
      </div>

      {(refundHistory.length > 0 || refundData.lastEligibleDate) && (
        <div className={styles.historyCard}>
          <div className={styles.historyHeader}>
            <svg
              className={styles.historyHeaderIcon}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 8V12L16 16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>ประวัติรับคืนยอดเสีย</span>
          </div>

          {refundHistory.length === 0 ? (
            <div className={styles.historyEmpty}>ไม่มีประวัติคืนยอดเสีย</div>
          ) : (
            <div className={styles.historyTable}>
              <div className={styles.historyTableHead}>
                <div>วันที่</div>
                <div>รอบ</div>
                <div>จำนวน</div>
                <div>สถานะ</div>
              </div>
              <div className={styles.historyTableBody}>
                {refundHistory.map((row) => (
                  <div key={row.id} className={styles.historyTableRow}>
                    <div>
                      {moment(row.created_at || row.cashBackDate)
                        .add(543, "year")
                        .format("DD-MM-YYYY HH:mm")}
                    </div>
                    <div>
                      {row.frequency || refundData.paidFrequency || "-"}
                    </div>
                    <div>
                      {Number(row.cashBackAmount || 0).toLocaleString("th-TH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div>{statusLabel(row.cashBackStatus)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.historyContent}>
            <p>
              รายการล่าสุด:{" "}
              {moment(refundData.lastEligibleDate)
                .add(543, "year")
                .format("DD-MM-YYYY HH:mm")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
